<template lang="pug">
div
    v-card
        v-card-title Individual Campaign User Settings
            v-spacer
            v-text-field(v-model="searchText" append-icon="mdi-magnify" label="Search by name" single-line hide-details @keyup.enter="performSearch" clearable @click:clear="clearSearch")
            v-btn.ml-3(color="primary" small @click="performSearch") Search

        v-data-table(
            :headers="headers"
            :loading="loading"
            :items="users"
            :options.sync="pagination"
            :server-items-length="totalUsers"
            loading-text="Loading users..."
            :footer-props="{ showCurrentPage: true, itemsPerPageOptions: [10, 25, 50] }"
            item-key="id"
        )
            template(v-slot:item.name="{ item }")
                v-avatar(size="32" style="margin-right: 10px;")
                    img(v-if="item.profile_picture_thumb || item.profile_picture" :src="item.profile_picture_thumb || item.profile_picture" @error="$event.target.style.display='none'")
                    v-icon(v-else color="grey") mdi-account-circle
                span {{ item.fname }} {{ item.lname }}

            template(v-slot:item.url="{ item }")
                span {{ item.url || '—' }}

            template(v-slot:item.tokens_per_rating="{ item }")
                v-edit-dialog(
                    :return-value.sync="item.tokens_per_rating"
                    large
                    persistent
                    @save="saveTokensPerRating(item)"
                    @cancel="cancelTokensPerRating"
                    @open="tempTokensPerRating = item.tokens_per_rating || defaultTPR"
                )
                    v-chip(small :color="item.tokens_per_rating != null ? 'purple' : 'grey'" text-color="white")
                        | {{ item.tokens_per_rating != null ? item.tokens_per_rating : defaultTPR }} T/R
                    span.ml-1.grey--text.text--darken-1(v-if="item.tokens_per_rating == null" style="font-size: 11px;") (default)
                    template(v-slot:input)
                        v-text-field(
                            v-model.number="tempTokensPerRating"
                            label="Tokens Per Rating"
                            type="number"
                            min="1"
                            single-line
                            counter
                            :rules="[rules.required, rules.minOne]"
                        )
                        v-btn.mt-1(x-small text color="grey" @click="resetToDefault") Reset to Default

            template(v-slot:item.actions="{ item }")
                v-btn(icon small color="grey" @click="resetUserTPR(item)" title="Reset to Default" :disabled="item.tokens_per_rating == null")
                    v-icon mdi-restore

        v-snackbar(top v-model="successMessage" color="success") {{ successText }}
        v-snackbar(top v-model="error.show" color="red") {{ error.message }}
</template>

<script>
export default {
    name: 'IndividualCampaignSettings',
    data() {
        return {
            searchText: '',
            tempTokensPerRating: 10,
            defaultTPR: 10,
            loading: false,
            users: [],
            totalUsers: 0,
            successMessage: false,
            successText: '',
            error: {
                show: false,
                message: ''
            },
            rules: {
                required: value => !!value || value === 0 || 'Required.',
                minOne: value => (value >= 1) || 'Must be at least 1'
            },
            headers: [
                {
                    text: 'User ID',
                    align: 'start',
                    sortable: false,
                    value: 'id',
                    width: 100
                },
                {
                    text: 'Name',
                    align: 'start',
                    sortable: false,
                    value: 'name',
                    width: 300
                },
                {
                    text: 'URL',
                    align: 'start',
                    sortable: false,
                    value: 'url',
                    width: 200
                },
                {
                    text: 'Tokens/Rating',
                    align: 'center',
                    sortable: false,
                    value: 'tokens_per_rating',
                    width: 180
                },
                {
                    text: 'Actions',
                    align: 'end',
                    sortable: false,
                    value: 'actions',
                    width: 100
                }
            ],
            pagination: {
                page: 1,
                itemsPerPage: 25,
                sortBy: [],
                sortDesc: []
            }
        };
    },
    watch: {
        pagination: {
            handler() {
                this.loadUsers();
            },
            deep: true
        }
    },
    async mounted() {
        await this.loadDefaultTPR();
        await this.loadUsers();
    },
    methods: {
        async loadDefaultTPR() {
            try {
                const result = await this.$store.dispatch('bh-campaign-settings/find', {
                    query: { setting_key: 'default_tokens_per_rating', $limit: 1 }
                });
                const items = result.data || result;
                if (items.length > 0) {
                    this.defaultTPR = parseInt(items[0].setting_value) || 10;
                }
            } catch (e) {
                console.error('Failed to load default TPR:', e);
            }
        },
        async loadUsers() {
            this.loading = true;
            try {
                const skip = (this.pagination.page - 1) * this.pagination.itemsPerPage;
                const limit = this.pagination.itemsPerPage;

                const query = {
                    terms: this.searchText || '',
                    name: 'yes',
                    $skip: skip,
                    $limit: limit
                };

                const result = await this.$store.dispatch('token-campaign-users/find', { query });

                // The service returns a flat array of user objects
                const items = Array.isArray(result) ? result : (result.data || []);

                this.users = items.map(u => {
                    const profile = u.result || u;
                    return {
                        id: u.id || profile.id,
                        fname: profile.fname || '',
                        lname: profile.lname || '',
                        url: profile.url || '',
                        profile_picture_thumb: profile.profile_picture_thumb || profile.profile_picture || null,
                        profile_picture: profile.profile_picture || null,
                        tokens_per_rating: u.tokens_per_rating != null ? u.tokens_per_rating : null,
                        _permissionId: null // Will be populated on save
                    };
                });

                // The token-campaign-users service doesn't return total count
                // So we estimate: if we got a full page, there's likely more
                this.totalUsers = items.length < limit ? skip + items.length : skip + items.length + 1;
            } catch (e) {
                console.error('Failed to load users:', e);
                this.error.message = `Failed to load users: ${e.message}`;
                this.error.show = true;
            } finally {
                this.loading = false;
            }
        },
        performSearch() {
            this.pagination.page = 1;
            this.loadUsers();
        },
        clearSearch() {
            this.searchText = '';
            this.pagination.page = 1;
            this.loadUsers();
        },
        cancelTokensPerRating() {
            this.tempTokensPerRating = this.defaultTPR;
        },
        resetToDefault() {
            this.tempTokensPerRating = null;
        },
        async saveTokensPerRating(item) {
            try {
                const tokensPerRating = this.tempTokensPerRating != null
                    ? parseInt(this.tempTokensPerRating)
                    : null;

                if (tokensPerRating != null && tokensPerRating < 1) {
                    throw new Error('Tokens per rating must be at least 1');
                }

                // Find the user's permissions record
                const permsResult = await this.$store.dispatch('users-permissions/find', {
                    query: { user_id: item.id, $limit: 1 }
                });
                const perms = permsResult.data || permsResult;

                if (perms.length > 0) {
                    await this.$store.dispatch('users-permissions/patch', [
                        perms[0].id,
                        { tokens_per_rating: tokensPerRating }
                    ]);
                }

                item.tokens_per_rating = tokensPerRating;
                const displayValue = tokensPerRating != null ? tokensPerRating : `Default (${this.defaultTPR})`;
                this.successText = `Updated tokens per rating to ${displayValue} for "${item.fname} ${item.lname}"`;
                this.successMessage = true;
            } catch (error) {
                this.error.message = `Error updating tokens per rating: ${error.message}`;
                this.error.show = true;
            }
        },
        async resetUserTPR(item) {
            try {
                const permsResult = await this.$store.dispatch('users-permissions/find', {
                    query: { user_id: item.id, $limit: 1 }
                });
                const perms = permsResult.data || permsResult;

                if (perms.length > 0) {
                    await this.$store.dispatch('users-permissions/patch', [
                        perms[0].id,
                        { tokens_per_rating: null }
                    ]);
                }

                item.tokens_per_rating = null;
                this.successText = `Reset tokens per rating to default for "${item.fname} ${item.lname}"`;
                this.successMessage = true;
            } catch (error) {
                this.error.message = `Error resetting tokens per rating: ${error.message}`;
                this.error.show = true;
            }
        }
    }
};
</script>
